import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUp,
  ArrowLeft,
  ChevronRight,
  List,
  Mail,
  Shield,
  Lock,
  Database,
  Zap,
  Scale,
  Globe,
  Building2,
  FileCheck,
  Eye,
  RefreshCw,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const NAV_SCROLL_OFFSET = 112;

const toc = [
  { id: 'privacy-overview', label: 'Privacy Policy',            icon: Eye,        number: '00' },
  { id: 'changes',          label: 'Changes to this statement', icon: RefreshCw,  number: '01' },
  { id: 'collection',       label: 'Collection & use',          icon: Database,   number: '02' },
  { id: 'cookies',          label: 'Cookies & tracking',        icon: Zap,        number: '03' },
  { id: 'rights',           label: 'Your data rights',          icon: Scale,      number: '04' },
  { id: 'security',         label: 'Security',                  icon: Lock,       number: '05' },
  { id: 'third-party',      label: 'Third-party links',         icon: Globe,      number: '06' },
  { id: 'transitions',      label: 'Business transitions',      icon: Building2,  number: '07' },
  { id: 'acceptance',       label: 'Acceptance of terms',       icon: FileCheck,  number: '08' },
  { id: 'contact',          label: 'Contact us',                icon: Mail,       number: '09' },
];

const GDPR_RIGHTS = [
  {
    title: 'The Right to Access',
    desc: 'You have the right to request copies of your personal data that we hold.',
  },
  {
    title: 'The Right to Rectification',
    desc: 'You have the right to request that we correct any information you believe is inaccurate.',
  },
  {
    title: 'The Right to Erasure ("Right to be Forgotten")',
    desc: 'You have the right to request that we erase your personal data, under certain conditions.',
  },
  {
    title: 'The Right to Opt-Out',
    desc: 'You have the right to direct us not to sell your personal information (note: SysUp360.com does not sell your identifiable email address to third-party marketers).',
  },
  {
    title: 'Non-Discrimination',
    desc: 'We will not discriminate against you for exercising any of these rights.',
  },
];

function getElementDocTop(el) {
  if (!el) return 0;
  return el.getBoundingClientRect().top + window.scrollY;
}

/* ─── Sub-components ──────────────────────────────────────────────────────── */

const SectionDivider = () => (
  <div className="flex items-center gap-4" aria-hidden>
    <div className="h-px flex-1 bg-gradient-to-r from-gray-800/0 via-gray-800/60 to-gray-800/0" />
    <div className="h-1 w-1 rounded-full bg-gray-700" />
    <div className="h-px flex-1 bg-gradient-to-r from-gray-800/0 via-gray-800/60 to-gray-800/0" />
  </div>
);

const SectionHeader = ({ icon: Icon, number, title }) => (
  <div className="mb-6 flex items-center gap-3.5">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F47D11]/12 ring-1 ring-[#F47D11]/25">
      <Icon className="h-5 w-5 text-[#F47D11]" aria-hidden />
    </div>
    <div>
      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-[#F47D11]/70">
        {number}
      </span>
      <h2 className="text-lg font-bold tracking-tight text-[#F47D11] sm:text-xl">{title}</h2>
    </div>
  </div>
);

const Section = ({ id, title, icon, number, children, accent = false }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
    className="scroll-mt-28 sm:scroll-mt-32"
  >
    <SectionHeader icon={icon} number={number} title={title} />
    <div
      className={`space-y-4 text-[15px] leading-[1.8] text-gray-300 sm:text-base ${
        accent ? 'rounded-2xl border border-[#F47D11]/20 bg-[#F47D11]/[0.06] p-5 sm:p-6' : ''
      }`}
    >
      {children}
    </div>
  </motion.section>
);

const TocItem = ({ item, isActive, onNavigate }) => {
  const Icon = item.icon;
  return (
    <li>
      <a
        href={`#${item.id}`}
        onClick={(e) => onNavigate(e, item.id)}
        aria-current={isActive ? 'location' : undefined}
        className={[
          'group flex items-center gap-2.5 rounded-lg py-2 pl-3 pr-3 text-left text-[13px] leading-snug transition-all duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F47D11]/35 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950',
          isActive
            ? 'bg-[#F47D11]/12 font-semibold text-[#F47D11]'
            : 'text-gray-500 hover:bg-gray-800/40 hover:text-gray-300',
        ].join(' ')}
      >
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-colors ${
            isActive ? 'text-[#F47D11]' : 'text-gray-600 group-hover:text-gray-400'
          }`}
          aria-hidden
        />
        <span className="flex-1">{item.label}</span>
        {isActive && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#F47D11]" aria-hidden />}
      </a>
    </li>
  );
};

const TocLinks = ({ activeId, onNavigate }) => (
  <ul className="space-y-0.5">
    {toc.map((item) => (
      <TocItem key={item.id} item={item} isActive={activeId === item.id} onNavigate={onNavigate} />
    ))}
  </ul>
);

/* ─── Page ────────────────────────────────────────────────────────────────── */

const PrivacyPolicy = () => {
  const [activeId, setActiveId] = useState(toc[0].id);
  const [readProgress, setReadProgress] = useState(0);
  const [showBackTop, setShowBackTop] = useState(false);

  const handleHashNav = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_SCROLL_OFFSET;
    window.history.replaceState(null, '', `#${id}`);
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const { scrollY } = window;
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      setReadProgress(total > 0 ? Math.min(100, Math.max(0, (scrollY / total) * 100)) : 0);
      setShowBackTop(scrollY > 480);

      const readerLineY = scrollY + NAV_SCROLL_OFFSET + 20;
      let current = toc[0].id;
      for (let i = 0; i < toc.length; i += 1) {
        const el = document.getElementById(toc[i].id);
        if (!el) continue;
        if (getElementDocTop(el) <= readerLineY) current = toc[i].id;
      }
      setActiveId(current);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const tocProgress =
    ((toc.findIndex((t) => t.id === activeId) + 1) / toc.length) * 100;

  return (
    <div className="relative min-h-screen bg-gray-900">
      {/* Reading progress bar */}
      <div
        className="pointer-events-none fixed left-0 right-0 top-0 z-[60] h-[3px] bg-gray-800/80"
        aria-hidden
      >
        <div
          className="h-full bg-gradient-to-r from-[#F47D11] to-[#F4733A] transition-[width] duration-100 ease-out"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      <Navbar />

      <main className="relative px-4 pb-28 pt-28 sm:px-6 sm:pb-32 sm:pt-32">
        {/* Ambient radial glow */}
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_45%_at_50%_-15%,rgba(244,125,17,0.11),transparent)]"
          aria-hidden
        />

        <div className="relative mx-auto max-w-7xl">

          {/* ── Hero card ──────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative mb-8 overflow-hidden rounded-3xl border border-gray-800/60 bg-gray-950/70 p-8 backdrop-blur-sm sm:p-12 lg:p-14"
          >
            {/* Grid pattern */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(244,125,17,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(244,125,17,.04) 1px, transparent 1px)',
                backgroundSize: '60px 60px',
              }}
              aria-hidden
            />
            {/* Ambient orbs */}
            <div
              className="pointer-events-none absolute -top-20 left-1/3 h-80 w-80 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(244,125,17,0.1), transparent 70%)' }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-16 right-1/4 h-64 w-64 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(circle, rgba(244,115,58,0.07), transparent 70%)' }}
              aria-hidden
            />

            <div className="relative z-10 flex flex-col items-start gap-10 lg:flex-row lg:items-center lg:justify-between">
              {/* Text block */}
              <div className="max-w-2xl">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#F47D11]/30 bg-[#F47D11]/10 px-3.5 py-1.5">
                  <Shield className="h-3.5 w-3.5 text-[#F47D11]" aria-hidden />
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#F47D11]">
                    Legal · Privacy Policy
                  </span>
                </div>

                <h1 className="mb-4 bg-gradient-to-r from-[#F47D11] via-[#F4733A] to-[#F47D11] bg-clip-text text-4xl font-black leading-[1.06] tracking-tight text-transparent sm:text-5xl lg:text-6xl">
                  Privacy Policy
                </h1>

                {/* Meta — wording matches legal document */}
                <div className="mb-8 flex flex-wrap items-center gap-2.5">
                  <span className="inline-flex items-center gap-2 rounded-full border border-gray-700/80 bg-gray-900/60 px-3.5 py-2 text-xs text-gray-300">
                    <span className="text-gray-500">Last updated</span>
                    <span aria-hidden className="text-gray-600">
                      –
                    </span>
                    <time dateTime="2026-02-01" className="font-semibold text-white">
                      February 2026
                    </time>
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-700/70 px-3.5 py-2 text-xs text-gray-400">
                    <span className="text-gray-500">Previous version</span>
                    <span aria-hidden className="text-gray-600">
                      –
                    </span>
                    <span className="font-medium text-gray-300">November 2025</span>
                  </span>
                </div>
              </div>

              {/* Shield icon graphic */}
              <div className="hidden shrink-0 flex-col items-center gap-3 lg:flex">
                <div className="relative flex h-28 w-28 items-center justify-center rounded-3xl bg-gradient-to-br from-[#F47D11]/20 to-[#F47D11]/5 ring-2 ring-[#F47D11]/20 shadow-2xl shadow-[#F47D11]/10">
                  <Shield className="h-14 w-14 text-[#F47D11]" aria-hidden />
                  <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5" />
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-600">
                  Privacy Protected
                </span>
              </div>
            </div>
          </motion.div>

          {/* ── Two-column layout ──────────────────────────────────────────── */}
          <div className="lg:grid lg:grid-cols-[minmax(220px,268px)_minmax(0,1fr)] lg:items-stretch lg:gap-10 xl:gap-14">

            {/* TOC sidebar: column stretches to article height so sticky pins for full read */}
            <aside className="hidden lg:block lg:min-h-0">
              <div className="lg:sticky lg:top-28 lg:z-20">
                <nav
                  aria-label="Page sections"
                  className="max-h-[calc(100vh-7.5rem)] overflow-x-hidden overflow-y-auto rounded-2xl border border-gray-800/50 bg-gray-950/40 py-4 pr-2 shadow-xl shadow-black/20 backdrop-blur-sm [scrollbar-width:thin] [scrollbar-color:rgba(244,125,17,0.35)_rgba(31,41,55,0.5)]"
                >
                  {/* Header */}
                  <div className="mb-2 flex items-center gap-2 px-4">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#F47D11]" aria-hidden />
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#F47D11]">
                      Contents
                    </p>
                    <div className="ml-auto rounded-full bg-gray-800/60 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                      {toc.length}
                    </div>
                  </div>

                  {/* Step progress */}
                  <div className="mx-4 mb-3 h-[3px] rounded-full bg-gray-800/80">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#F47D11] to-[#F4733A] transition-[width] duration-300 ease-out"
                      style={{ width: `${tocProgress}%` }}
                      aria-hidden
                    />
                  </div>

                  <TocLinks activeId={activeId} onNavigate={handleHashNav} />
                </nav>
              </div>
            </aside>

            {/* Main article */}
            <div className="min-w-0">
              {/* Mobile collapsible TOC */}
              <details className="group mb-6 overflow-hidden rounded-2xl border border-gray-800/80 bg-gray-950/60 lg:hidden">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 text-sm font-semibold text-[#F47D11] marker:hidden [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center gap-2">
                    <List className="h-4 w-4 text-[#F47D11]" aria-hidden />
                    Jump to a section
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-500 transition-transform group-open:rotate-90" />
                </summary>
                <div className="border-t border-gray-800/80 bg-gray-900/40 px-2 py-3">
                  <nav aria-label="Page sections">
                    <TocLinks activeId={activeId} onNavigate={handleHashNav} />
                  </nav>
                </div>
              </details>

              {/* Article card */}
              <div className="rounded-2xl border border-gray-800/60 bg-gradient-to-b from-gray-950/60 via-gray-950/30 to-gray-950/20 p-6 shadow-2xl shadow-black/30 sm:p-8 lg:p-10">
                <div className="space-y-10">

                  {/* 00 — opening (title appears in hero) */}
                  <motion.section
                    id="privacy-overview"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                    className="scroll-mt-28 sm:scroll-mt-32"
                  >
                    <p className="text-[15px] leading-[1.8] text-gray-300 sm:text-base [text-wrap:pretty]">
                      This Privacy Policy explains our policy regarding the privacy of information
                      supplied by users or collected by us from users of this web site or from other
                      third parties. Because we want to build users&apos; trust and confidence in our
                      privacy practices, we want to disclose them to you.
                    </p>
                  </motion.section>

                  <SectionDivider />

                  {/* 01 */}
                  <Section id="changes" title="Changes in this Privacy Statement" icon={RefreshCw} number="01">
                    <p>
                      If we decide to change our privacy policy, we will post those changes to this
                      privacy statement, the home page, and other places we deem appropriate so that
                      you are aware of what information we collect, how we use it, and under what
                      circumstances, if any, we disclose it.
                    </p>
                    <p>
                      We reserve the right to modify this privacy statement at any time, so please
                      review it frequently. If we make material changes to this policy, we will notify
                      you here, by email, or by means of a notice on our home page.
                    </p>
                  </Section>

                  <SectionDivider />

                  {/* 02 */}
                  <Section id="collection" title="Collection & Use of information" icon={Database} number="02">
                    <p>
                      SysUp360.com respects your right to privacy. SysUp360.com received your
                      information from your submission to us. SysUp360.com is the owner of the
                      information that you provide in any area of our website. Your email address and
                      any other identifying information that you give us will not be revealed to any
                      third party.
                    </p>
                    <p>
                      We use the information that you provide primarily to send you, our newsletters
                      or to personalize your experience. We may also collect and report to third
                      parties (such as affiliates, content and service providers, and advertisers)
                      aggregated information from our web site. For example, we may aggregate and
                      report to third parties that X people purchased a particular product during a
                      month, or that Y% of visitors to this web site are between the ages 25-34.
                    </p>
                    <p>
                      From time to time, we may provide you with the opportunity to participate in
                      surveys on our site. If you participate, we will request certain personally
                      identifiable information from you. Participation in these surveys or contests is
                      completely voluntary and you, therefore, have a choice whether to submit this
                      information.
                    </p>
                    <p>
                      As is true of most web sites, we also gather certain information automatically
                      and store it in log files. This information includes internet protocol (IP)
                      addresses, browser type, internet service provider (ISP), referring/exit pages,
                      operating system, date/time stamp, and click stream data. We use this
                      information, which does not identify individual users, to analyze trends, to
                      administer the site, to track users&apos; movements around the site and to
                      gather demographic information about our user base. We do link this
                      automatically collected data to personally identifiable information like IP
                      addresses and email addresses. However, this is exclusively for internal use.
                    </p>
                    <p>
                      Finally, SysUp360.com may disclose subscriber information in response to
                      subpoenas, court orders, and other legal processes.
                    </p>
                  </Section>

                  <SectionDivider />

                  {/* 03 */}
                  <Section id="cookies" title="Cookies & Tracking Technologies" icon={Zap} number="03">
                    <p>
                      A cookie is a small text file that is stored on a user&apos;s computer for
                      record-keeping purposes. We do not use cookies on this site. This privacy
                      statement covers the use of cookies by this site only and does not cover the use
                      of cookies by any advertisers.
                    </p>
                    <p>
                      We use cookies and similar tracking technologies (such as Google Analytics,
                      Facebook Pixels, and standard WordPress session cookies) to track activity on
                      our website and hold certain information. Cookies are files with a small amount
                      of data which may include an anonymous unique identifier.
                    </p>
                    <p className="font-semibold text-[#F47D11]">We use these to:</p>
                    <ul className="list-disc space-y-2 pl-5 text-gray-300 marker:text-[#F47D11]">
                      <li>Analyze our website traffic and user behavior.</li>
                      <li>Remember your preferences and settings.</li>
                      <li>Provide targeted advertisements based on your interests.</li>
                    </ul>
                    <p>
                      You can instruct your browser to refuse all cookies or to indicate when a cookie
                      is being sent. However, if you do not accept cookies, you may not be able to use
                      some portions of our site.
                    </p>
                  </Section>

                  <SectionDivider />

                  {/* 04 */}
                  <Section id="rights" title="Your Data Protection Rights (GDPR & CCPA)" icon={Scale} number="04">
                    <p>
                      Depending on your location, you may have the following rights regarding your
                      personal information:
                    </p>
                    <ul className="space-y-4 text-gray-300">
                      {GDPR_RIGHTS.map((right) => (
                        <li
                          key={right.title}
                          className="rounded-xl border border-gray-800/60 bg-gray-900/40 p-4 sm:p-4"
                        >
                          <span className="text-[15px] leading-[1.8] sm:text-base">
                            <strong className="font-semibold text-[#F47D11]">{right.title}:</strong>{' '}
                            {right.desc}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p>
                      To exercise any of these rights, please contact us at{' '}
                      <a
                        href="mailto:hello@sysup360.com"
                        className="font-semibold text-[#F47D11] underline decoration-[#F47D11]/40 underline-offset-[3px] transition-colors hover:text-[#F4733A]"
                      >
                        hello@sysup360.com
                      </a>
                      . We will respond to your request within 30 days.
                    </p>
                  </Section>

                  <SectionDivider />

                  {/* 05 */}
                  <Section id="security" title="Security of Your Data" icon={Lock} number="05">
                    <p>
                      The security of your data is important to us. We implement a variety of
                      security measures to maintain the safety of your personal information, including
                      the use of SSL (Secure Socket Layer) encryption to protect data transmitted to
                      and from our site.
                    </p>
                    <p>
                      While we strive to use commercially acceptable means to protect your personal
                      information, please be aware that no method of transmission over the Internet or
                      method of electronic storage is 100% secure. We conduct periodic reviews of our
                      security practices to ensure your data is handled safely and in accordance with
                      this Privacy Policy.
                    </p>
                  </Section>

                  <SectionDivider />

                  {/* 06 */}
                  <Section id="third-party" title="Third-Party Links" icon={Globe} number="06">
                    <p>
                      For your convenience, our surveys or newsletters may contain links to
                      third-party web sites that are not owned or controlled by us. We are not
                      responsible for the privacy practices of these other web sites. We encourage you
                      to note when you follow links in our newsletters and to read the privacy
                      statements of these other web sites. We encourage you to be aware when you
                      leave our newsletters or surveys and to read the privacy statements of each and
                      every web site that collects personally identifiable information. This Privacy
                      Policy applies solely to information collected by SysUp360.com.
                    </p>
                  </Section>

                  <SectionDivider />

                  {/* 07 */}
                  <Section id="transitions" title="Business Transitions" icon={Building2} number="07">
                    <p>
                      In the event SysUp360.com goes through a business transition, such as a merger,
                      acquisition by another company, or sale of all or a portion of its assets, your
                      personal identifiable information will likely be transferred among the assets.
                      You will be notified via prominent notice on our web site prior to any such
                      change in ownership or control of your personal information.
                    </p>
                  </Section>

                  <SectionDivider />

                  {/* 08 */}
                  <Section id="acceptance" title="Acceptance of Terms" icon={FileCheck} number="08">
                    <p>
                      By submitting information on any area of our website, you agree to the Privacy
                      Policy. If you do not agree to this policy, please do not submit your
                      information. We reserve the right, at our discretion, to change, modify, add,
                      or remove portions of this policy at any time. All Privacy Policy changes will
                      take effect immediately upon their posting on the site. Please check this page
                      periodically for changes.
                    </p>
                  </Section>

                  <SectionDivider />

                  {/* 09 */}
                  <Section id="contact" title="Contact Us" icon={Mail} number="09" accent>
                    <p className="text-gray-300">
                      If you have any questions or suggestions regarding our Privacy Policy, please
                      contact us via email –{' '}
                      <a
                        href="mailto:hello@sysup360.com"
                        className="font-semibold text-[#F47D11] underline decoration-[#F47D11]/40 underline-offset-[3px] transition-colors hover:text-[#F4733A]"
                      >
                        hello@sysup360.com
                      </a>
                    </p>
                  </Section>

                </div>
              </div>

              {/* Footer nav */}
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-lg text-sm font-semibold text-[#F47D11] transition-colors hover:text-[#F4733A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F47D11]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Back to home
                </Link>
                <p className="text-xs text-gray-600">
                  <span className="hidden lg:inline">
                    ~6 min read · Sidebar tracks your progress through the page
                  </span>
                  <span className="lg:hidden">
                    ~6 min read · Use &quot;Jump to a section&quot; above to navigate
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Back-to-top button */}
      <AnimatePresence>
        {showBackTop && (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 14, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed right-4 bottom-24 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#F47D11] to-[#F4733A] text-white shadow-lg shadow-orange-950/40 ring-2 ring-white/10 transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 sm:right-6 sm:bottom-24 md:bottom-28 lg:bottom-24"
            aria-label="Back to top"
          >
            <ArrowUp className="h-5 w-5" aria-hidden />
          </motion.button>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
